import { useEffect, useRef, useState, memo } from "react";
import * as echarts from "echarts";
import CustomTooltip from "../Buttons/CustomTooltip";

const ConversionLineChart = memo(({ data }) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const chartInstance = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const resizeObserverRef = useRef(null);

  // Initialize and update chart
  useEffect(() => {
    if (!chartRef.current || !data?.length) return;

    const initChart = () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
      
      chartInstance.current = echarts.init(chartRef.current);

      // Calculate maximum value for scaling the bars
      const maxValue = Math.max(
        ...data.map((item) => Math.max(item.job_count, item.total_file_count))
      );

      const option = {
        grid: {
          left: "3%",
          right: "4%",
          top: "15%",
          bottom: "3%",
          containLabel: true,
        },
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const dayData = data.find((d) => d.day === params[0].axisValue);
            return `
      <strong>${params[0].axisValue}</strong><br/>
      Total Jobs: ${dayData.job_count}<br/>
      Total Files: ${dayData.total_file_count}
    `;
          },
        },
        xAxis: {
          type: "category",
          data: data.map((item) => item.day),
          axisLabel: {
            rotate: 45,
          },
        },
        yAxis: {
          type: "value",
          name: "Count",
        },
        series: [
          // Background bars (purple)
          {
            type: "bar",
            barWidth: "30%",
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: "#A963CF",
                },
                {
                  offset: 0.001,
                  color: "#A963CF",
                },
                {
                  offset: 0.02,
                  color: "rgba(177, 98, 206, 0.13)",
                },
                {
                  offset: 1,
                  color: "rgba(177, 98, 206, 0.13)",
                },
              ]),
            },
            data: data.map(() => maxValue * 1.1),
            animation: false,
            silent: true,
            z: 0,
          },
          // Jobs line
          {
            name: "Jobs",
            type: "line",
            smooth: true,
            data: data.map((item) => item.job_count),
            lineStyle: {
              color: "#5FCE2F",
              width: 3,
            },
            symbol: "circle",
            symbolSize: 8,
            itemStyle: {
              color: "#5FCE2F",
            },
            z: 2,
          },
          // Files line
          {
            name: "Files",
            type: "line",
            smooth: true,
            data: data.map((item) => item.total_file_count),
            lineStyle: {
              color: "#8298DD",
              width: 3,
            },
            symbol: "diamond",
            symbolSize: 8,
            itemStyle: {
              color: "#8298DD",
            },
            z: 2,
          },
        ],
        legend: {
          data: ["Files", "Jobs"],
          top: 0,
        },
      };

      chartInstance.current.setOption(option);

      // Set up resize observer for the container
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;
          if (chartInstance.current && width > 0 && height > 0) {
            // Use requestAnimationFrame for smoother resize
            requestAnimationFrame(() => {
              chartInstance.current.resize();
            });
          }
        }
      });

      if (chartRef.current) {
        resizeObserverRef.current.observe(chartRef.current);
      }

      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
        chartInstance.current?.dispose();
      };
    };

    initChart();
  }, [data]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement;
      setIsFullscreen(!!fullscreenElement);
      
      // Double resize with delays to ensure proper rendering
      setTimeout(() => {
        if (chartInstance.current) {
          chartInstance.current.resize();
          
          // Second resize after a short delay to catch any rendering issues
          setTimeout(() => {
            chartInstance.current.resize();
          }, 100);
        }
      }, 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Fullscreen toggle
  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current
        .requestFullscreen()
        .then(() => {
          // Multiple resize calls to ensure proper rendering
          setTimeout(() => {
            if (chartInstance.current) {
              chartInstance.current.resize();
              
              setTimeout(() => {
                chartInstance.current.resize();
              }, 50);
            }
          }, 100);
        })
        .catch(console.error);
    } else {
      document
        .exitFullscreen()
        .then(() => {
          // Multiple resize calls to ensure proper rendering
          setTimeout(() => {
            if (chartInstance.current) {
              chartInstance.current.resize();
              
              setTimeout(() => {
                chartInstance.current.resize();
              }, 50);
            }
          }, 100);
        })
        .catch(console.error);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`card card-migration ${isFullscreen ? "fullscreen-mode" : ""}`}
      style={{
        position: "relative",
        marginBottom: "0.2rem",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="p-3 mb-0" style={{ borderRadius: "8px", flex: 1 }}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="d-flex align-items-center dbcardheadsdesign ">
            <div className="icons-bg me-2">
              <i className="dash-icon-database-migrated"></i>
            </div>
            <span >Code Blocks Converted or Units Converted</span>
          </h6>
          <CustomTooltip
            content={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            placement={isFullscreen?"right":"left"}
            
          >
            <i
              className={`mdi ${
                isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
              } text-dark font-size-22 fullscreen`}
              style={{ cursor: "pointer" }}
              onClick={handleFullscreen}
            />
          </CustomTooltip>
        </div>
        {!data || data.length === 0 ? (
          <div
            style={{
              height: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                width: "99%",
              }}
            >
              <img
                src="assets/images/emptystate.gif"
                style={{ width: "200px", opacity: 0.7 }}
                alt="No data available"
              />
              <div className="text-muted mt-2">
                No conversion data available
              </div>
            </div>
          </div>
        ) : (
          <div
            ref={chartRef}
            style={{
              width: "100%",
              height: isFullscreen ? "85vh" : "300px",
              marginTop: isFullscreen ? "0px" : "30px",
            }}
          />
        )}
      </div>
    </div>
  );
});

export default ConversionLineChart;